import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { MutateKeys } from "../constants/MutateKeys";
import { useMutation, useQuery } from "@tanstack/react-query";
import { generatePath } from "react-router-dom";
import {
  allowOnlyEmptyString,
  cleanParams,
} from "../Utilities/queryParamUtils";
import {
  CreateCustomerPayloadType,
  CustomerDatabaseListingParams,
} from "../Types/customerDatabaseTypes";
import { ArchiveKeys } from "../enums/ArchiveActionKeys";
import {
  AddDatabaseTableEntriesParamsType,
  ContactTableServiceResponseType,
  DeleteDatabaseTableEntriesParamsType,
  DocumentsTableServiceResponseType,
  EditContactParamsType,
  FetchDatabaseTableType,
  JobTableDataResponseType,
  JobTableServiceResponseType,
  NotesTableServiceResponseType,
  RolesMetaServiceResponseType,
} from "../Types/DatabaseTabTypes";
import { DatabaseSections } from "../enums/DatabaseEnums";
import { SectionAccessKeys } from "../enums/AccessPathEnums";

const CustomerDatabaseService = () => {
  const {
    DATABASE_TABLE_ROUTE,
    DATABASE_META_ROUTE,
    DELETE_DATABASE_TABLE_ROUTE,
    GET_CUSTOMER_DATABASE,
    SYNC_CUSTOMER_DATABASE,
    ARCHIVE_CUSTOMER_ENTRY,
    GET_SPECIFIC_CUSTOMER,
    GET_CUSTOMER_DETAILS_FROM_GOOGLE,
    GET_ACCOUNT_TERMS_OPTIONS,
    ARCHIVE_DATABASE_ENTRY,
  } = ApiRoutes;

  const {
    FETCH_CONTACTS_TABLES_KEY,
    FETCH_DOCUMENTS_TABLES_KEY,
    FETCH_NOTES_TABLES_KEY,
    FETCH_DATABASE_META_KEY,
    FETCH_JOB_TABLES_KEY,
    GET_CUSTOMER_DATABASE_KEY,
    GET_CUSTOMER_PROFILE_KEY,
  } = QueryKeys;

  const {
    CREATE_CUSTOMER_ENTRY_KEY,
    ARCHIVE_CUSTOMER_ENTRY_KEY,
    SYNC_CUSTOMER_DATABASE_KEY,
    GET_CUSTOMER_GOOGLE_DETAILS_KEY,
    UPDATE_CUSTOMER_DETAILS_KEY,
    UPDATE_CUSTOMER_NOTES_KEY,
    GET_ACCOUNT_TERMS_OPTIONS_KEY,
    GET_CUSTOMER_DETAILS_EDIT_KEY,
    ADD_DATABASE_ENTRIES_KEY,
    DELETE_DATABASE_ENTRIES_KEY,
    EDIT_DATABASE_ENTRIES_KEY,
  } = MutateKeys;

  const GetCustomerDatabaseEntries = (
    params: CustomerDatabaseListingParams,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const getCustomerDatabaseEntries = async () => {
      const cleanedParams = cleanParams({
        ...params,
        setDefaultPageSize: pageSizeChanged,
      });
      const { data } = await axiosInstance.get(GET_CUSTOMER_DATABASE, {
        params: cleanedParams,
      });
      return data;
    };

    return useQuery({
      queryFn: getCustomerDatabaseEntries,
      queryKey: [GET_CUSTOMER_DATABASE_KEY, params],
      refetchOnWindowFocus: false,
      enabled: !!enabledFlag,
    });
  };

  const CreateCustomerProfile = () => {
    const createCustomerProfile = async (
      payloadData: CreateCustomerPayloadType,
    ) => {
      const payload = {
        entityData: {
          ...cleanParams(payloadData),
        },
      };
      const data = await axiosInstance.post(GET_CUSTOMER_DATABASE, payload);
      return data;
    };

    return useMutation({
      mutationFn: createCustomerProfile,
      mutationKey: [CREATE_CUSTOMER_ENTRY_KEY],
    });
  };

  const GetCustomerDetailsFromGoogle = () => {
    const getCustomerDetailsFromGoogle = async (accountLink: string) => {
      const { data } = await axiosInstance.get(
        GET_CUSTOMER_DETAILS_FROM_GOOGLE,
        {
          params: {
            url: accountLink,
          },
        },
      );
      return data;
    };

    return useMutation({
      mutationFn: getCustomerDetailsFromGoogle,
      mutationKey: [GET_CUSTOMER_GOOGLE_DETAILS_KEY],
    });
  };

  const ArchiveCustomerProfile = () => {
    const archiveCustomerProfile = async ({
      customerId,
      notes,
    }: {
      customerId: string;
      notes: string;
    }) => {
      const payload = {
        entityData: {
          notes: notes,
          deletedTable: ArchiveKeys.CUSTOMER,
        },
      };
      const data = await axiosInstance.post(
        generatePath(ARCHIVE_CUSTOMER_ENTRY, { customerId: customerId }),
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: archiveCustomerProfile,
      mutationKey: [ARCHIVE_CUSTOMER_ENTRY_KEY],
    });
  };

  const SyncCustomerDatabaseList = () => {
    const syncCustomerDatabaseList = async () => {
      const data = await axiosInstance.post(SYNC_CUSTOMER_DATABASE);
      return data;
    };

    return useMutation({
      mutationFn: syncCustomerDatabaseList,
      mutationKey: [SYNC_CUSTOMER_DATABASE_KEY],
    });
  };

  const GetSpecificCustomerDetailsForEdit = () => {
    const getSpecificCustomerDetailsForEdit = async (customerId: string) => {
      const { data } = await axiosInstance.get(
        generatePath(GET_SPECIFIC_CUSTOMER, { customerId: customerId }),
      );
      return data;
    };

    return useMutation({
      mutationFn: getSpecificCustomerDetailsForEdit,
      mutationKey: [GET_CUSTOMER_DETAILS_EDIT_KEY],
    });
  };

  const GetSpecificCustomerDetails = (customerId: string) => {
    const getSpecificCustomerDetails = async () => {
      const { data } = await axiosInstance.get(
        generatePath(GET_SPECIFIC_CUSTOMER, { customerId: customerId }),
      );
      return data;
    };

    return useQuery({
      queryKey: [GET_CUSTOMER_PROFILE_KEY],
      queryFn: getSpecificCustomerDetails,
      refetchOnWindowFocus: false,
    });
  };

  const UpdateCustomerProfile = () => {
    const updateCustomerProfile = async ({
      customerId,
      payloadData,
    }: {
      customerId: string;
      payloadData: CreateCustomerPayloadType;
    }) => {
      const payload = {
        entityData: {
          ...allowOnlyEmptyString(payloadData),
        },
      };
      const data = await axiosInstance.put(
        generatePath(GET_SPECIFIC_CUSTOMER, { customerId: customerId }),
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: updateCustomerProfile,
      mutationKey: [UPDATE_CUSTOMER_DETAILS_KEY],
    });
  };

  const UpdateCustomerProfileNotes = () => {
    const updateCustomerProfileNotes = async ({
      customerId,
      payloadData,
    }: {
      customerId: string;
      payloadData: string;
    }) => {
      const payload = {
        entityData: {
          notes: payloadData,
        },
      };
      const data = await axiosInstance.patch(
        generatePath(GET_SPECIFIC_CUSTOMER, { customerId: customerId }),
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: updateCustomerProfileNotes,
      mutationKey: [UPDATE_CUSTOMER_NOTES_KEY],
    });
  };

  const GetAccountTermsOptions = ({ isCrm = false }: { isCrm: boolean }) => {
    const getAccountTermsOptions = async () => {
      const url = isCrm
        ? `${GET_ACCOUNT_TERMS_OPTIONS}?section=${SectionAccessKeys.CRM}`
        : GET_ACCOUNT_TERMS_OPTIONS;
      const { data } = await axiosInstance.get(url);
      const formattedOptions = data?.data?.map((item) => ({
        label: item,
        value: item,
      }));
      return formattedOptions;
    };

    return useMutation({
      mutationFn: getAccountTermsOptions,
      mutationKey: [GET_ACCOUNT_TERMS_OPTIONS_KEY],
    });
  };

  const FetchContactsTable = (params?: FetchDatabaseTableType) => {
    const fetchContactsTable =
      async (): Promise<ContactTableServiceResponseType> => {
        const url = generatePath(DATABASE_TABLE_ROUTE, {
          tableType: DatabaseSections.CONTACTS,
        });
        const cleanedParams = cleanParams(params);
        const { data } =
          await axiosInstance.get<ContactTableServiceResponseType>(url, {
            params: cleanedParams,
          });
        return data;
      };
    return useQuery({
      queryKey: [FETCH_CONTACTS_TABLES_KEY],
      queryFn: fetchContactsTable,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!params?.section_id,
    });
  };

  const FetchDocumentsTable = (params?: FetchDatabaseTableType) => {
    const fetchDocumentsTable =
      async (): Promise<DocumentsTableServiceResponseType> => {
        const url = generatePath(DATABASE_TABLE_ROUTE, {
          tableType: DatabaseSections.DOCUMENTS,
        });
        const cleanedParams = cleanParams(params);
        const { data } =
          await axiosInstance.get<DocumentsTableServiceResponseType>(url, {
            params: cleanedParams,
          });
        return data;
      };
    return useQuery({
      queryKey: [FETCH_DOCUMENTS_TABLES_KEY],
      queryFn: fetchDocumentsTable,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!params?.section_id,
    });
  };

  const FetchNotesTable = (params?: FetchDatabaseTableType) => {
    const fetchNotesTable =
      async (): Promise<NotesTableServiceResponseType> => {
        const url = generatePath(DATABASE_TABLE_ROUTE, {
          tableType: DatabaseSections.NOTES,
        });
        const cleanedParams = cleanParams(params);
        const { data } = await axiosInstance.get<NotesTableServiceResponseType>(
          url,
          {
            params: cleanedParams,
          },
        );
        return data;
      };
    return useQuery({
      queryKey: [FETCH_NOTES_TABLES_KEY],
      queryFn: fetchNotesTable,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!params?.section_id,
    });
  };

  const FetchJobsTable = (params?: FetchDatabaseTableType) => {
    const fetchJobsTable = async (): Promise<JobTableServiceResponseType> => {
      const url = generatePath(DATABASE_TABLE_ROUTE, {
        tableType: DatabaseSections.JOBS,
      });
      const cleanedParams = cleanParams(params);
      const { data } = await axiosInstance.get<JobTableServiceResponseType>(
        url,
        {
          params: cleanedParams,
        },
      );
      return data;
    };
    return useQuery({
      queryKey: [FETCH_JOB_TABLES_KEY, params],
      queryFn: fetchJobsTable,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!params?.section_id,
    });
  };

  const FetchContactRoles = ({ isCrm = false }: { isCrm?: boolean }) => {
    const fetchContactRoles =
      async (): Promise<RolesMetaServiceResponseType> => {
        const url = isCrm
          ? `${DATABASE_META_ROUTE}?type=roles&section=${SectionAccessKeys.CRM}`
          : `${DATABASE_META_ROUTE}?type=roles`;
        const { data } =
          await axiosInstance.post<RolesMetaServiceResponseType>(url);
        return data;
      };
    return useQuery({
      queryKey: [FETCH_DATABASE_META_KEY],
      queryFn: fetchContactRoles,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });
  };

  const AddDatabaseTableEntries = () => {
    const addDatabaseTableEntries = async (
      params?: AddDatabaseTableEntriesParamsType,
    ) => {
      const { id, section, section_id, ...rest } = params;
      const url = generatePath(DATABASE_TABLE_ROUTE, {
        tableType: id,
      });
      const queryParamURL = `${url}?section=${section}&section_id=${section_id}`;
      const payload = {
        entityData: {
          ...cleanParams(rest),
        },
      };
      const { data } = await axiosInstance.post(queryParamURL, payload);
      return data;
    };
    return useMutation({
      mutationFn: addDatabaseTableEntries,
      mutationKey: [ADD_DATABASE_ENTRIES_KEY],
    });
  };

  const DeleteDatabaseTableEntries = () => {
    const deleteDatabaseTableEntries = async (
      params?: DeleteDatabaseTableEntriesParamsType,
    ) => {
      const { id, ...rest } = params;
      const url = generatePath(ARCHIVE_DATABASE_ENTRY, {
        id: id,
      });
      const cleanedParams = cleanParams(rest);
      const payload = {
        entityData: {
          ...cleanedParams,
        },
      };
      const { data } = await axiosInstance.post(url, payload);
      return data;
    };
    return useMutation({
      mutationFn: deleteDatabaseTableEntries,
      mutationKey: [DELETE_DATABASE_ENTRIES_KEY],
    });
  };

  const EditDatabaseTableEntries = () => {
    const editDatabaseTableEntries = async (params?: EditContactParamsType) => {
      const { id, entityId, section, section_id, ...rest } = params;
      const url = generatePath(DELETE_DATABASE_TABLE_ROUTE, {
        tableType: id,
        entityId: entityId,
      });
      const queryParamURL = `${url}?section=${section}&section_id=${section_id}`;
      const payload = {
        entityData: rest,
      };
      const { data } = await axiosInstance.put(queryParamURL, payload);
      return data;
    };
    return useMutation({
      mutationFn: editDatabaseTableEntries,
      mutationKey: [EDIT_DATABASE_ENTRIES_KEY],
    });
  };

  return {
    GetCustomerDatabaseEntries,
    GetCustomerDetailsFromGoogle,
    CreateCustomerProfile,
    ArchiveCustomerProfile,
    SyncCustomerDatabaseList,
    GetSpecificCustomerDetails,
    GetSpecificCustomerDetailsForEdit,
    UpdateCustomerProfile,
    UpdateCustomerProfileNotes,
    GetAccountTermsOptions,
    FetchContactsTable,
    FetchDocumentsTable,
    FetchNotesTable,
    FetchContactRoles,
    AddDatabaseTableEntries,
    DeleteDatabaseTableEntries,
    EditDatabaseTableEntries,
    FetchJobsTable,
  };
};

export default CustomerDatabaseService;
